import React, { useContext, useEffect, useMemo } from "react";
import { Container } from "react-bootstrap";
import { Link, Navigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { InfoContext } from "../providers/InfosContextProvider";
import newsArticles from "../assets/data/newsManifest.json";

const getLocalizedValue = (value, language) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return value[language] || value.PT || value.EN || "";
};

const formatDate = (dateString, language) => {
  const locale = language === "EN" ? "en-GB" : "pt-PT";

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(dateString));
};

const toPermalink = (article) => `/news/${article.date}/${article.slug}`;

const getExcerpt = (article, language) => {
  const body = getLocalizedValue(article.body, language);

  if (Array.isArray(body)) {
    return body[0] || "";
  }

  return body;
};

const getArticleClassName = (index) => {
  if (index % 6 === 0) {
    return "news-tile news-tile--large";
  }

  if (index % 5 === 0) {
    return "news-tile news-tile--tall";
  }

  return "news-tile";
};

const News = () => {
  const { texts, language } = useContext(InfoContext);
  const { date, slug } = useParams();
  const articles = useMemo(
    () =>
      [...newsArticles].sort(
        (left, right) => new Date(right.date) - new Date(left.date),
      ),
    [],
  );

  const activeArticle =
    date && slug
      ? articles.find(
          (article) => article.date === date && article.slug === slug,
        )
      : null;
  const landingPath = "/news";

  useEffect(() => {
    if (activeArticle) {
      const articleTitle = getLocalizedValue(activeArticle.title, language);
      document.title = `${articleTitle} | Casas do Nabão`;
    } else {
      document.title = `${texts.newsPage?.title || "News"} | Casas do Nabão`;
    }
  }, [language, activeArticle, texts.newsPage?.title]);

  if (date && slug && !activeArticle) {
    return <Navigate to={landingPath} replace />;
  }

  if (!activeArticle) {
    return (
      <>
        <Header />
        <main className="news-page news-page--landing">
          <Container className="py-5">
            <section className="news-hero mx-auto mb-5">
              <h1 className="news-title mb-3">{texts.newsPage?.title}</h1>
              <p className="news-hero-text mb-0">
                {language === "EN"
                  ? "A monthly selection of market updates and legal news, open the article that matters to you."
                  : "Uma seleção mensal de novidades do mercado e temas legais. Abra o artigo que lhe interessar."}
              </p>
            </section>

            <section
              className="news-masonry"
              aria-label={texts.newsPage?.title}
            >
              {articles.map((article, index) => {
                const title = getLocalizedValue(article.title, language);
                const imageAlt =
                  getLocalizedValue(article.imageAlt, language) || title;
                const excerpt = getExcerpt(article, language);

                return (
                  <Link
                    key={article.slug}
                    to={toPermalink(article)}
                    className={getArticleClassName(index)}
                  >
                    <div className="news-tile-image-wrap">
                      <img
                        src={article.image}
                        alt={imageAlt}
                        className="news-tile-image"
                        loading="lazy"
                      />
                    </div>
                    <div className="news-tile-content">
                      <div className="news-tile-meta">
                        <span>{formatDate(article.date, language)}</span>
                      </div>
                      <h2 className="news-tile-title">{title}</h2>
                      <p className="news-tile-excerpt">{excerpt}</p>
                    </div>
                  </Link>
                );
              })}
            </section>
          </Container>
        </main>
        <Footer />
      </>
    );
  }

  const articleTitle = getLocalizedValue(activeArticle.title, language);
  const articleBody = getLocalizedValue(activeArticle.body, language);
  const articleSource = getLocalizedValue(
    activeArticle.source?.label,
    language,
  );
  const articleImageAlt =
    getLocalizedValue(activeArticle.imageAlt, language) || articleTitle;
  const otherArticles = articles.filter(
    (article) => article.slug !== activeArticle.slug,
  );

  return (
    <>
      <Header />
      <main className="news-page">
        <Container className="py-5">
          <section className="news-featured mx-auto">
            <h1 className="news-title mb-3">{articleTitle}</h1>
            <div className="news-meta mb-4">
              <span>{formatDate(activeArticle.date, language)}</span>
            </div>
            <div className="news-image-wrap mb-4">
              <img
                src={activeArticle.image}
                alt={articleImageAlt}
                className="news-image"
              />
            </div>
            <div className="news-body">
              {Array.isArray(articleBody)
                ? articleBody.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))
                : articleBody}
            </div>
            <div className="news-source mt-4">
              <span>{texts.newsPage?.sourceLabel}:</span>
              <a
                href={activeArticle.source?.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {articleSource}
              </a>
            </div>
          </section>

          <section className="news-others mx-auto mt-5">
            <h2 className="news-section-title mb-4">
              {texts.newsPage?.otherNews}
            </h2>
            {otherArticles.length > 0 ? (
              <div className="news-related-grid">
                {otherArticles.map((article) => {
                  const title = getLocalizedValue(article.title, language);
                  const imageAlt =
                    getLocalizedValue(article.imageAlt, language) || title;

                  return (
                    <Link
                      key={article.slug}
                      to={toPermalink(article)}
                      className="news-card news-card--compact"
                    >
                      <div className="news-card-image-wrap">
                        <img
                          src={article.image}
                          alt={imageAlt}
                          className="news-card-image"
                          loading="lazy"
                        />
                      </div>
                      <div className="news-card-title">{title}</div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <p className="news-empty mb-0">{texts.newsPage?.noOtherNews}</p>
            )}
          </section>
        </Container>
      </main>
      <Footer />
    </>
  );
};

export default News;
